import { useCallback, useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { CHEMICAL_DB_API_HOST, JSON_HEADERS, MIXES_API } from './constants';
import type { MixSummary, MixesResponse } from './types';

const useMixes = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [mixes, setMixes] = useState<MixSummary[]>([]);
    const { authenticatedGet } = useFetch({
        apiHost: CHEMICAL_DB_API_HOST,
        headers: JSON_HEADERS
    });
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetch = useCallback(
        async (params?: { status?: 'ACTIVE' | 'ARCHIVED' }): Promise<MixSummary[] | null> => {
            try {
                setIsLoading(true);
                const status = params?.status ?? 'ACTIVE';

                const response = await withRetry(
                    () => authenticatedGet<MixesResponse>(`${MIXES_API}?status=${status}`),
                    CONNECTION_ERROR,
                    async () => {
                        await fetch(params);

                        return null;
                    }
                );

                const mixList = response?.mixes ?? [];

                setMixes(mixList);

                return mixList;
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedGet, withRetry, CONNECTION_ERROR]
    );

    return {
        fetch,
        isLoading,
        mixes
    };
};

export default useMixes;
